import React, { useState, useEffect, useCallback } from 'react';
import config from '../../../config';  // ✅ هذا صحيح
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import {
  Save,
  Business,
  CloudUpload,
  Delete,
  CheckCircle,
  Phone,
  Email,
  Language,
  LocationOn
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import axios from 'axios';

const translations = {
  ar: {
    title: 'معلومات الشركة',
    subtitle: 'إدارة بيانات الشركة والهوية التجارية',
    basicInfo: 'المعلومات الأساسية',
    contactInfo: 'معلومات الاتصال',
    legalInfo: 'المعلومات القانونية',
    companyLogo: 'شعار الشركة',
    companyNameEn: 'اسم الشركة (English)',
    companyNameAr: 'اسم الشركة (عربي)',
    addressEn: 'العنوان (English)',
    addressAr: 'العنوان (عربي)',
    phone: 'الهاتف',
    mobile: 'الجوال',
    email: 'البريد الإلكتروني',
    website: 'الموقع الإلكتروني',
    commercialRegister: 'السجل التجاري',
    taxNumber: 'الرقم الضريبي',
    uploadLogo: 'رفع الشعار',
    deleteLogo: 'حذف الشعار',
    saveChanges: 'حفظ التغييرات',
    saveSuccess: 'تم حفظ معلومات الشركة بنجاح',
    saveError: 'فشل حفظ معلومات الشركة',
    loadError: 'فشل تحميل معلومات الشركة',
    maxSize: 'الحد الأقصى: 5 ميجابايت',
    formats: 'JPG, PNG, GIF, WEBP'
  },
  en: {
    title: 'Company Information',
    subtitle: 'Manage company data and business identity',
    basicInfo: 'Basic Information',
    contactInfo: 'Contact Information',
    legalInfo: 'Legal Information',
    companyLogo: 'Company Logo',
    companyNameEn: 'Company Name (English)',
    companyNameAr: 'Company Name (Arabic)',
    addressEn: 'Address (English)',
    addressAr: 'Address (Arabic)',
    phone: 'Phone',
    mobile: 'Mobile',
    email: 'Email',
    website: 'Website',
    commercialRegister: 'Commercial Register',
    taxNumber: 'Tax Number',
    uploadLogo: 'Upload Logo',
    deleteLogo: 'Delete Logo',
    saveChanges: 'Save Changes',
    saveSuccess: 'Company information saved successfully',
    saveError: 'Failed to save company information',
    loadError: 'Failed to load company information',
    maxSize: 'Max size: 5 MB',
    formats: 'JPG, PNG, GIF, WEBP'
  }
};

export default function CompanyInfoTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    companyName: '',
    companyNameAr: '',
    address: '',
    addressAr: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    commercialRegister: '',
    taxNumber: '',
    logoUrl: ''
  });

  const loadCompanySettings = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/CompanySettings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        companyName: response.data.companyName || '',
        companyNameAr: response.data.companyNameAr || '',
        address: response.data.address || '',
        addressAr: response.data.addressAr || '',
        phone: response.data.phone || '',
        mobile: response.data.mobile || '',
        email: response.data.email || '',
        website: response.data.website || '',
        commercialRegister: response.data.commercialRegister || '',
        taxNumber: response.data.taxNumber || '',
        logoUrl: response.data.logoUrl || ''
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.loadError });
    } finally {
      setLoading(false);
    }
  }, [t.loadError]);

  useEffect(() => {
    loadCompanySettings();
  }, [loadCompanySettings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      const token = localStorage.getItem('token');
      await axios.put(`${config.API_BASE_URL}/CompanySettings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: t.saveSuccess });
    } catch (error: any) {
      setMessage({ type: 'error', text: t.saveError });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size exceeds 5MB' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);

      const response = await axios.post(
        `${config.API_BASE_URL}/CompanySettings/logo`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFormData(prev => ({
        ...prev,
        logoUrl: response.data.logoUrl
      }));

      setMessage({ type: 'success', text: 'Logo uploaded successfully' });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    }
  };

  const handleDeleteLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/CompanySettings/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFormData(prev => ({
        ...prev,
        logoUrl: ''
      }));

      setMessage({ type: 'success', text: 'Logo deleted successfully' });
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to delete logo' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Business sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {t.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {t.subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Logo Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  p: 1.5,
                  borderRadius: 2
                }}
              >
                <Business sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.companyLogo}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
              <Avatar
                src={formData.logoUrl ? `${config.API_ORIGIN}${formData.logoUrl}` : ''}
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  border: '3px solid rgba(102, 126, 234, 0.2)'
                }}
              >
                <Business sx={{ fontSize: 60, color: '#667eea' }} />
              </Avatar>

              <Stack spacing={2} flex={1}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  {t.uploadLogo}
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoUpload}
                  />
                </Button>

                {formData.logoUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteLogo}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    {t.deleteLogo}
                  </Button>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={t.maxSize} size="small" />
                  <Chip label={t.formats} size="small" variant="outlined" />
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Basic Info Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  bgcolor: 'rgba(118, 75, 162, 0.1)',
                  p: 1.5,
                  borderRadius: 2
                }}
              >
                <CheckCircle sx={{ color: '#764ba2', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.basicInfo}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t.companyNameEn}
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.companyNameAr}
                  name="companyNameAr"
                  value={formData.companyNameAr}
                  onChange={handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t.addressEn}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.addressAr}
                  name="addressAr"
                  value={formData.addressAr}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Contact Info Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  p: 1.5,
                  borderRadius: 2
                }}
              >
                <Phone sx={{ color: '#667eea', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.contactInfo}
              </Typography>
            </Stack>

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t.phone}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: '#667eea' }} />
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.mobile}
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: '#764ba2' }} />
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={t.email}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: '#667eea' }} />
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  label={t.website}
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <Language sx={{ mr: 1, color: '#764ba2' }} />
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Legal Info Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              transform: 'translateY(-4px)'
            }
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  bgcolor: 'rgba(118, 75, 162, 0.1)',
                  p: 1.5,
                  borderRadius: 2
                }}
              >
                <LocationOn sx={{ color: '#764ba2', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">
                {t.legalInfo}
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label={t.commercialRegister}
                name="commercialRegister"
                value={formData.commercialRegister}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                label={t.taxNumber}
                name="taxNumber"
                value={formData.taxNumber}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
            }
          }}
        >
          {t.saveChanges}
        </Button>
      </Box>
    </Box>
  );
}